import { Notification } from '@beeboard/common';

// Extended notification type that includes parentEntityTitle
interface ExtendedNotification extends Notification {
  parentEntityTitle?: string | null;
  parentEntityId?: string | null;
}

export const useNotificationFormatter = () => {
  // Helper function to escape HTML to prevent XSS
  function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;

    return div.innerHTML;
  }

  // Get plain text dispatcher name for vue-i18n interpolation
  function getDispatcherName(notification: ExtendedNotification): string {
    return notification.dispatcher
      ? `${notification.dispatcher.first_name} ${notification.dispatcher.last_name}`
      : $t('users.deletedUser');
  }

  // Get plain text entity title for vue-i18n interpolation
  function getEntityTitle(notification: ExtendedNotification): string {
    const rawEntityTitle = notification.entityTitle || '';

    if (rawEntityTitle && rawEntityTitle.length > 65) {
      return rawEntityTitle.substring(0, 65) + '...';
    }

    return rawEntityTitle;
  }

  // Get plain text parent entity title (e.g., project name) for vue-i18n interpolation
  function getParentEntityTitle(notification: ExtendedNotification): string {
    // Handle null or undefined
    if (!notification.parentEntityTitle) {
      return '';
    }

    const rawParentTitle = notification.parentEntityTitle;

    if (rawParentTitle.length > 65) {
      return rawParentTitle.substring(0, 65) + '...';
    }

    return rawParentTitle;
  }

  // Format the already-interpolated notification text with HTML styling
  function formatNotification(
    notification: ExtendedNotification,
    interpolatedText: string,
    includeEntityLink: boolean = false,
  ): string {
    const dispatcherName = getDispatcherName(notification);
    const entityTitle = getEntityTitle(notification);
    const parentEntityTitle = getParentEntityTitle(notification);
    const isEntityDeleted = notification.entityTitle === '[deleted]';

    // Escape the names to prevent XSS
    const escapedDispatcher = escapeHtml(dispatcherName);
    const escapedEntity = escapeHtml(entityTitle);
    const escapedParentEntity = escapeHtml(parentEntityTitle);

    // Create styled versions
    const styledDispatcher = `<span class="dispatcher-name">${escapedDispatcher}</span>`;
    const styledParentEntity = parentEntityTitle
      ? `<span class="parent-entity-name">${escapedParentEntity}</span>`
      : '';

    let styledEntity: string;
    if (isEntityDeleted) {
      styledEntity = `<span class="entity-details entity-deleted">${escapedEntity}</span>`;
    } else if (includeEntityLink) {
      styledEntity = `<span class="entity-details entity-link entity-clickable">${escapedEntity}</span>`;
    } else {
      styledEntity = `<span class="entity-details entity-link">${escapedEntity}</span>`;
    }

    // Replace the plain text with styled versions
    // We need to be careful to replace the exact text that was interpolated
    let result = interpolatedText;

    // Replace dispatcher
    if (dispatcherName) {
      result = result.replace(dispatcherName, styledDispatcher);
    }

    // Replace entity
    if (entityTitle) {
      result = result.replace(entityTitle, styledEntity);
    }

    // Only replace parent entity if it exists and was included in the interpolation
    if (parentEntityTitle && result.includes(parentEntityTitle)) {
      result = result.replace(parentEntityTitle, styledParentEntity);
    }

    return result;
  }

  return {
    formatNotification,
    getDispatcherName,
    getEntityTitle,
    getParentEntityTitle,
  };
};
